import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { KanbanCard } from './KanbanCard';

interface Stage {
  stage_code: string;
  label: string;
  color: string;
  allowed_transitions: string[];
}

interface KanbanColumnProps {
  stage: Stage;
  apps: any[];
  statusField: string;
  showLoanAmount?: boolean;
  dragOverStatus: string | null;
  onDragOver: (e: React.DragEvent, stageCode: string) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, stageCode: string) => void;
  onDragStart: (id: number) => void;
  onDragEnd: () => void;
  onCardClick: (id: number) => void;
  stages?: Stage[];
  onStatusChange?: (appId: number, newStatus: string) => void;
  bulkMode?: boolean;
  selectedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
  wipLimit?: number;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  stage,
  apps,
  statusField,
  showLoanAmount,
  dragOverStatus,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  onCardClick,
  stages,
  onStatusChange,
  bulkMode,
  selectedIds,
  onToggleSelect,
  wipLimit,
}) => {
  const isDragOver = dragOverStatus === stage.stage_code;
  const overWip = wipLimit != null && wipLimit > 0 && apps.length > wipLimit;

  return (
    <div
      className={`flex-shrink-0 w-72 lg:w-auto lg:min-w-[240px] lg:flex-1 lg:max-w-xs flex flex-col rounded-xl transition-all ${
        isDragOver ? 'ring-2 ring-blue-400 bg-blue-50' : overWip ? 'bg-red-50 ring-1 ring-red-200' : 'bg-gray-50'
      }`}
      onDragOver={(e) => onDragOver(e, stage.stage_code)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, stage.stage_code)}
    >
      {/* Column header */}
      <div className="p-3 flex items-center gap-2">
        <div className={`w-3 h-3 rounded-full ${stage.color}`} />
        <h3 className="font-medium text-sm text-gray-700 truncate">{stage.label}</h3>
        <span className={`ml-auto text-xs rounded-full px-2 py-0.5 ${
          overWip ? 'bg-red-200 text-red-700 font-semibold' : 'bg-gray-200 text-gray-400'
        }`}>
          {apps.length}{wipLimit ? `/${wipLimit}` : ''}
        </span>
        {overWip && <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />}
      </div>

      {/* Cards */}
      <div className="flex-1 px-2 pb-2 space-y-2 overflow-y-auto max-h-[calc(100vh-240px)]">
        {apps.map(app => (
          <KanbanCard
            key={app.id}
            app={app}
            statusField={statusField}
            showLoanAmount={showLoanAmount}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onClick={onCardClick}
            stages={stages}
            currentStageCode={stage.stage_code}
            onStatusChange={onStatusChange}
            bulkMode={bulkMode}
            selected={selectedIds?.has(app.id)}
            onToggleSelect={onToggleSelect}
          />
        ))}
        {apps.length === 0 && (
          <div className="text-center text-xs text-gray-400 py-8">Brak</div>
        )}
      </div>
    </div>
  );
};
